import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { AxiosError } from 'axios'
import { useUIStore } from '../stores/uiStore'
import { useAuthStore } from '../stores/authStore'
import { fetchCurrentUser, logout } from '../api/auth'
import Toast from './Toast'
import NavItem from './ui/NavItem'
import StatusBadge from './ui/StatusBadge'
import {
  Home,
  BookOpen,
  Settings,
  Menu,
  Moon,
  Sun,
  LogOut,
  FlaskConical,
} from 'lucide-react'

function Layout() {
  const { theme, toggleTheme, sidebarOpen, toggleSidebar } = useUIStore()
  const user = useAuthStore((s) => s.user)
  const authLogout = useAuthStore((s) => s.logout)
  const updateUser = useAuthStore((s) => s.updateUser)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    if (!isAuthenticated) return
    let active = true
    fetchCurrentUser().then((freshUser) => {
      if (active) updateUser(freshUser)
    }).catch((error: unknown) => {
      if (active && error instanceof AxiosError && error.response?.status === 401) {
        authLogout()
        navigate('/login', { replace: true })
      }
    })
    return () => { active = false }
  }, [authLogout, isAuthenticated, navigate, updateUser])

  const handleLogout = async () => {
    try {
      await logout()
    } catch {
      // Ignore errors; local logout always proceeds
    }
    authLogout()
    navigate('/login')
  }

  const isAdmin = user?.role === 'Admin'
  const pageTitle =
    pathname === '/' ? 'Dashboard' :
    pathname.startsWith('/studies') ? 'Studies' :
    pathname === '/admin' ? 'Administration' :
    'Protocol Decompiler'

  return (
    <div className={`min-h-screen flex ${theme === 'dark' ? 'dark' : ''}`}>
      <aside
        className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`}
        aria-label="Main navigation"
      >
        <div className="p-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-primary-600" aria-hidden="true" />
              <h1 className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
                Protocol Decompiler
              </h1>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <Menu className="h-5 w-5 text-gray-600 dark:text-gray-300" aria-hidden="true" />
          </button>
        </div>
        <nav className="mt-4 space-y-1 px-2 flex-1">
          <NavItem to="/" icon={Home} label="Dashboard" collapsed={!sidebarOpen} end />
          <NavItem to="/studies" icon={BookOpen} label="Studies" collapsed={!sidebarOpen} />
          {isAdmin && (
            <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <NavItem to="/admin" icon={Settings} label="Admin" collapsed={!sidebarOpen} />
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ${
              !sidebarOpen ? 'justify-center' : ''
            }`}
          >
            <LogOut className="h-5 w-5 shrink-0" aria-hidden="true" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-between shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {pageTitle}
          </h2>
          <div className="flex items-center gap-3">
            {user && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-300 hidden sm:inline">
                  {user.display_name}
                </span>
                <StatusBadge status={user.role} size="sm" />
              </div>
            )}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Toggle theme"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4 text-gray-600 dark:text-gray-300" aria-hidden="true" />
              ) : (
                <Sun className="h-4 w-4 text-gray-300" aria-hidden="true" />
              )}
            </button>
          </div>
        </header>

        <main className="flex-1 p-6 bg-gray-50 dark:bg-gray-900 overflow-auto">
          <Outlet />
        </main>
      </div>
      <Toast />
    </div>
  )
}

export default Layout
